import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../redux/app/hooks';
import { setSelectedClassRoomId, setSelectedClassRoomName, setTeacherMainRoute } from '../redux/slices/authSlice';
import { UPDATE_ACTIVE_STATUS } from '../pages/CommonChats/api/ChatsQuery';
import { FETCH_POLICY_NO_CACHE } from '../utils/constants';
import { markClassSwitched } from '../utils/helper';

const TEACHER_SELECT_CLASS_PATH = '/teacher-select-class';

/**
 * Takes a teacher back to the class code page to switch class, without logging out.
 * - Chat status is set to inactive (as on logout); it becomes active again when the teacher opens the Chats tab
 *   in the class they choose next (the classroom page sets it from the selected tab).
 * - The current class is cleared (classroom and subject), and the teacher's main route is set to the class code
 *   page so the route guard lets them open it while logged in.
 */
export const useTeacherChangeClass = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [updateChatActiveStatus] = useMutation(UPDATE_ACTIVE_STATUS, { fetchPolicy: FETCH_POLICY_NO_CACHE });

  return useCallback(async () => {
    // Sent before the class is cleared; a failure never blocks switching
    await updateChatActiveStatus({ variables: { chatActive: false } }).catch(() => undefined);
    // A meeting open in another tab/window sees this and ends or leaves (like it does on logout)
    markClassSwitched();
    dispatch(setTeacherMainRoute({ teacherMainRoute: TEACHER_SELECT_CLASS_PATH }));
    dispatch(setSelectedClassRoomId({ selectedClassRoomId: '' }));
    dispatch(setSelectedClassRoomName({ selectedClassRoomName: '' }));
    localStorage.removeItem('selectedSId');
    navigate(TEACHER_SELECT_CLASS_PATH);
  }, [dispatch, navigate, updateChatActiveStatus]);
};
